import React from 'react';
import {
  loadingToError,
  loadingToSuccess,
  loadingToWarning
} from '@components/Notify/Toast';
import Notify from '@components/Notify/ActionNotify';
import { createAsyncOperationTracker, refreshAffectedList } from '../createAsyncOperationTracker';

import {
  normalizeDeleteResult,
  buildDeleteResultMessage,
  hasDeleteFailures,
  isBlockedDeleteResult,
  notifyDeleteResult
} from '../deleteResultMessage';

const BULK_DELETE_STATUS_TYPE = 'bulk-delete.status';

export const bulkDeleteTracker = createAsyncOperationTracker({
  prefix: 'bulk-delete',
  loadingMessage: 'Deleting records... You will be notified once it completes',
  pendingTtl: 120000,
  completedTtl: 120000,
  toastDuration: 70000
});

// Backward-compatible named exports (mirror the original app-side module so
// consumers keep importing the same names via the app-side re-export shim).
export const getBulkDeletePayload = bulkDeleteTracker.getPayload;
export const isQueuedBulkDeleteResponse = bulkDeleteTracker.isQueuedResponse;
export const getBulkDeleteToastId = bulkDeleteTracker.getToastId;
export const handleQueuedBulkDeleteResponse = bulkDeleteTracker.handleQueuedResponse;
export const completeBulkDeleteOperation = bulkDeleteTracker.completeOperation;

/**
 * True when a `bulk-delete.status` notification is terminal — the status the
 * handler actually acts on (final toast + list refresh). Non-terminal / interim
 * statuses are ignored. Exposed so app-specific wrappers can gate their own
 * refresh with exactly the same condition the handler uses.
 */
export const isTerminalBulkDeleteStatus = (data) => {
  const result = normalizeDeleteResult(data);
  return data?.status === 'done' || hasDeleteFailures(result);
};

export const bulkDeleteHandlers = {
  [BULK_DELETE_STATUS_TYPE]: (data, { dispatch } = {}) => {
    if (!isTerminalBulkDeleteStatus(data)) return;
    const operationId = data?.operation_id;
    const context = completeBulkDeleteOperation(operationId, data);
    const toastId = operationId ? getBulkDeleteToastId(operationId) : null;
    const result = normalizeDeleteResult(data);
    const message = buildDeleteResultMessage(result || data);
    const completeFailure = hasDeleteFailures(result);
    const hasMoreReasons = result?.not_deleted?.length > 2;
    const toastOptions = hasMoreReasons ? { duration: 8000 } : undefined;

    if (completeFailure) {
      if (context && toastId) {
        if (isBlockedDeleteResult(result)) loadingToWarning(toastId, message, { ...toastOptions, icon: null });
        else loadingToError(toastId, message, toastOptions);
      } else {
        notifyDeleteResult(result);
      }
    } else if (data?.status === 'done') {
      if (context && toastId) loadingToSuccess(toastId, message);
      else Notify.Success({ message });
    } else {
      return;
    }

    refreshAffectedList(context);
    // App-specific refresh hook (replaces the old SystemSettings coupling).
    context?.onComplete?.(data, { dispatch });
  }
};
